import React from 'react';
import { useNavigate } from 'react-router';
import {
  Grid,
  Banner,
  Button,
  useTheme,
  useMediaQuery,
} from '@forgeglobal/design-framework';
import DetailCard, {
  DetailCardItem,
} from '@tcs/common/components/molecules/DetailCard';
import ChangePasswordForm from '@tcs/client-portal/components/forms/ChangePasswordForm';
import SecurityQuestionsForm from '@tcs/client-portal/components/forms/SecurityQuestionsForm';
import DialogLayout from '@tcs/client-portal-common/components/organisms/DialogLayout';
import useDialogOpen from '@tcs/client-portal-common/hooks/useDialogOpen';
import {
  useChangePassword,
  useUpdateQuestion,
} from '@tcs/client-portal-common/hooks/auth';
import useStyles from './ProfileSecurity.style';

interface ProfileSecurityProps {
  question: string;
  loading?: boolean;
  setQuestion: (value: string) => void;
  showMessage: (value: string) => void;
}

const ProfileSecurity = ({
  question,
  loading,
  setQuestion,
  showMessage,
}: ProfileSecurityProps) => {
  const classes = useStyles();
  const changePassword = useChangePassword();
  const nav = useNavigate();
  const updateQuestion = useUpdateQuestion();
  const theme = useTheme();
  const medium = useMediaQuery(theme.breakpoints.up('md'));
  const changePasswordDialogName = 'change-password';
  const { setDialogOpen: setChangePasswordDialogOpen } = useDialogOpen(
    changePasswordDialogName
  );
  const securityQuestionDialogName = 'change-security-question';
  const { setDialogOpen: setSecurityQuestionDialogOpen } = useDialogOpen(
    securityQuestionDialogName
  );

  const handleChangePassword = async (values: any) => {
    const { currentPassword, newPassword } = values;
    await changePassword.mutateAsync({
      currentPassword,
      newPassword,
    });
    showMessage('Your password has successfully changed.');
    setChangePasswordDialogOpen(false);
  };

  const handleUpdateQuestion = (values: any) => {
    setQuestion(values?.question);
    showMessage('Your security question was changed successfully.');
    updateQuestion.mutate(values);
    setSecurityQuestionDialogOpen(false);
  };

  if (updateQuestion.isSuccess) {
    nav('/profile/security');
  }

  function getSecurityBanner() {
    if (question == null || question === '') {
      return (
        <div className={classes.bannerWrapper}>
          <Banner severity="warning" bannerType="inline">
            Please update your security question.
          </Banner>
        </div>
      );
    }
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <DetailCard
            title="Password"
            action={
              <Button
                data-test-id="change-password-button"
                color="secondary"
                onClick={() => setChangePasswordDialogOpen(true)}>
                {medium ? 'Change Password' : 'Change'}
              </Button>
            }>
            <DetailCardItem label="A strong password is required to protect your confidential data." />
          </DetailCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <DetailCard
            title="Security Question"
            action={
              <Button
                data-test-id="change-questions-button"
                color="secondary"
                onClick={() => setSecurityQuestionDialogOpen(true)}>
                {medium ? 'Change Question' : 'Change'}
              </Button>
            }
            loading={loading}>
            {getSecurityBanner()}
            <div key={'security-question'} className={classes.questionGroup}>
              <DetailCardItem
                label={'Question'}
                value={question}
                className={classes.textAligned}
              />
              <DetailCardItem label={'Answer'} value={'*'.repeat(20)} />
            </div>
          </DetailCard>
        </Grid>
      </Grid>
      <DialogLayout
        shouldShowUser
        setShowDialog={setChangePasswordDialogOpen}
        name={changePasswordDialogName}>
        <div className={classes.container}>
          <ChangePasswordForm
            onSubmit={handleChangePassword}
            onCancel={() => setChangePasswordDialogOpen(false)}
            showPasswordFail={changePassword.isError}
          />
        </div>
      </DialogLayout>
      <DialogLayout
        shouldShowUser
        setShowDialog={setSecurityQuestionDialogOpen}
        name={securityQuestionDialogName}>
        <div className={classes.container}>
          <SecurityQuestionsForm
            onSubmit={handleUpdateQuestion}
            onCancel={() => setSecurityQuestionDialogOpen(false)}
            showUpdateQuestionFail={updateQuestion.isError}
          />
        </div>
      </DialogLayout>
    </>
  );
};

export default ProfileSecurity;
